import React, { ReactNode } from 'react';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import { IconChevron, IconPendoInfo } from '../../../../../../components/Icons';
import './InfoCard.scss';

/**
 * Type for interaction types
 */
export type InfoCardInteractionType = 'info' | 'actions';

/**
 * Props for the InfoCard component
 */
export interface InfoCardProps {
  /** Whether to show a bullet point in the first column */
  showListSymbol?: boolean;
  /** Optional tag label to display */
  tag?: string;
  /** Optional date to display */
  date?: string;
  /** Whether to show the info icon */
  showInfoIcon?: boolean;
  /** Handler for interactions with the card */
  onInteraction?: (type: InfoCardInteractionType, data?: any, children?: ReactNode | null) => void;
  /** The main message content, can contain HTML that will be sanitized */
  message: string;
  /** Whether to display the info as a card or a paragraph */
  isACard?: boolean;
  /** Optional CSS class name */
  className?: string;
  /** Optional recommended actions */
  recommendedActions?: {
    title: string;
    actions: {
      label: string;
      message: string;
    }[];
  };
}

/**
 * Utility function to format URLs in text to be clickable links
 */
const formatUrlsInText = (text: string | undefined): string => {
  // If text is undefined or null, return an empty string
  if (!text) {
    return '';
  }

  // URL regex pattern
  const urlPattern =
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/g;

  return text.replace(urlPattern, url => {
    // If the URL is already wrapped in an <a> tag, return it as is
    if (url.startsWith('<a') && url.endsWith('</a>')) {
      return url;
    }

    // Otherwise, wrap it in an <a> tag
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });
};

/**
 * InfoCard component for displaying information in a card format
 * Can be used for simple paragraphs or more structured content
 */
export const InfoCard: React.FC<InfoCardProps> = ({
  showListSymbol = false,
  tag,
  date,
  showInfoIcon = false,
  onInteraction,
  message,
  isACard = false,
  className,
  recommendedActions,
}) => {
  // Configure DOMPurify for safe HTML rendering
  const purifyConfig = {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'a', 'ul', 'ol', 'li', 'p', 'br', 'strike', 'del', 'div'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class'],
    ADD_ATTR: ['target', 'rel'],
    KEEP_CONTENT: true,
  };

  // Sanitize and format the message content
  const sanitizedContent = formatUrlsInText(DOMPurify.sanitize(message, purifyConfig));
  const parsedContent = { __html: sanitizedContent };

  const handleRecommendedActionsClick = () => {
    if (recommendedActions) {
      // Create a React element with the recommended actions using an ordered list
      const actionsContent = (
        <div className="recommended-actions-content">
          <ul className="info-card__recommended-actions-list">
            {recommendedActions.actions.map((action, index) => (
              <li key={index} className="info-card__recommended-actions-item">
                <div className="info-card__recommended-action" role="button" tabIndex={0} aria-label={action.label}>
                  {action.label}
                </div>
                {action.message && <div className="info-card__recommended-action-message">{action.message}</div>}
              </li>
            ))}
          </ul>
        </div>
      );

      onInteraction && onInteraction('actions', recommendedActions, actionsContent);
    }
  };

  return (
    <div className={classNames('info-card', { card: isACard }, className)}>
      {showListSymbol && (
        <div className={classNames('info-card__list-symbol', { 'tag-date': !(tag || date) })}>
          <span className="info-card__bullet"></span>
        </div>
      )}
      <div className="info-card__content">
        {(tag || date) && (
          <div className="info-card__header">
            <div className="info-card__tag-group">
              {tag && <span className="info-card__tag">{tag}</span>}
              {date && <span className="info-card__date">{date}</span>}
            </div>
          </div>
        )}
        <div className="info-card__message" dangerouslySetInnerHTML={parsedContent} />
        {recommendedActions && recommendedActions?.actions.length > 0 && (
          <div
            className="info-card__recommended-actions"
            onClick={() => handleRecommendedActionsClick()}
            role="button"
            tabIndex={0}
            aria-label={recommendedActions.title}
          >
            {recommendedActions.actions.length > 1
              ? recommendedActions.actions.length + ' ' + recommendedActions.title
              : recommendedActions.title}
            <IconChevron />
          </div>
        )}
      </div>
      {showInfoIcon && (
        <div
          className="info-card__info-icon"
          onClick={() => onInteraction && onInteraction('info')}
          role="button"
          tabIndex={0}
          aria-label="More information"
        >
          <IconPendoInfo />
        </div>
      )}
    </div>
  );
};

export default InfoCard;
